/**
 * PublicPlayerCard — standalone downloadable player card.
 * Iter 35: the on-screen preview is CSS-scaled to fit any device width, and
 * the PNG export rasterises an off-screen, unscaled 900×1260 clone — so the
 * captured geometry never depends on the device viewport or scroll position.
 * Fixes clipped badges/header text in exports and broken mobile downloads.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import html2canvas from "html2canvas";
import { DownloadSimple, Eye, EyeSlash, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/components/ImageInput";
import { brandLogoFor } from "@/pages/overlays/_shared";

const CARD_W = 900;   // export width in px — locked so the raster is identical on every device
const CARD_H = 1260;  // 3:4.2 poster ratio, roomy footer + stats grid

export default function PublicPlayerCard() {
  const { auctionId, playerId } = useParams();
  const [data, setData] = useState(null);
  const [showPoints, setShowPoints] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(1);
  const cardRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    api.get(`/public/auctions/${auctionId}`).then((r) => setData(r.data)).catch(() => setData({}));
  }, [auctionId]);

  // Scale the fixed-size card down to the container width (mobile preview).
  useEffect(() => {
    const update = () => {
      if (wrapRef.current) setScale(Math.min(1, wrapRef.current.clientWidth / CARD_W));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [data]);

  const { player, team, auction } = useMemo(() => {
    if (!data) return {};
    const player = (data.players || []).find((p) => p.id === playerId);
    const team = player ? (data.teams || []).find((t) => t.id === player.sold_to) : null;
    return { player, team, auction: data.auction };
  }, [data, playerId]);

  if (!data) return <FullPage>Loading player…</FullPage>;
  if (!player) return <FullPage>Player not found. <Link to={`/auction/${auctionId}`} className="text-yellow-300 underline ml-2">Back</Link></FullPage>;

  const isIcon = player.is_icon || player.category === "Icon";
  const pts = player.sold_price != null ? player.sold_price : player.base_price;
  const proxiedPhoto = player.photo_url ? resolveImageUrl(player.photo_url) : null;
  const proxiedTeamLogo = team?.logo_url ? resolveImageUrl(team.logo_url) : null;
  const proxiedBrand = brandLogoFor(auction);
  const hasCustomBrand = auction?.allow_custom_led_logo && auction?.custom_led_logo_url;

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    let clone = null;
    try {
      await document.fonts?.ready;
      // Rasterise an off-screen, unscaled clone so capture geometry is
      // independent of the preview transform, viewport size and scroll.
      clone = cardRef.current.cloneNode(true);
      clone.removeAttribute("data-testid");
      clone.querySelectorAll("[data-testid]").forEach((n) => n.removeAttribute("data-testid"));
      clone.style.transform = "none";
      clone.style.position = "fixed";
      clone.style.left = "-10000px";
      clone.style.top = "0";
      clone.style.margin = "0";
      document.body.appendChild(clone);
      // Preload every image in the clone via the backend proxy → base64 data
      // URL. This bypasses cross-origin (Google Drive etc.) tainting so the
      // photos actually appear in the exported PNG.
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const toDataUrl = async (url) => {
        try {
          if (!url || url.startsWith("data:")) return url;
          const target = url.startsWith(window.location.origin)
            ? url
            : `${backend}/api/proxy-image?url=${encodeURIComponent(url)}`;
          const r = await fetch(target);
          if (!r.ok) return url;
          const blob = await r.blob();
          return await new Promise((res) => {
            const reader = new FileReader();
            reader.onloadend = () => res(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch {
          return url;
        }
      };
      const cloneImgs = [...clone.querySelectorAll("img")];
      const dataUrls = await Promise.all(cloneImgs.map((el) => toDataUrl(el.getAttribute("src"))));
      cloneImgs.forEach((el, i) => {
        el.src = dataUrls[i];
        // The preview's onError hides broken images with display:none — the
        // clone inherits that, so un-hide them now that src is a data URL.
        el.style.display = "";
      });
      await new Promise((r) => setTimeout(r, 300));
      await Promise.all(
        [...clone.querySelectorAll("img")].map((img) =>
          img.complete && img.naturalWidth > 0
            ? null
            : new Promise((res) => { img.onload = res; img.onerror = res; })
        )
      );
      const canvas = await html2canvas(clone, {
        backgroundColor: "#071731",
        scale: 2,
        useCORS: true,
        allowTaint: false,
        width: CARD_W,
        height: CARD_H,
        windowWidth: CARD_W,
        windowHeight: CARD_H,
      });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) throw new Error("Could not render image on this device");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `${player.name.replace(/\s+/g, "_")}_card.png`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert("Failed to export card: " + (e?.message || e));
    } finally {
      if (clone) clone.remove();
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" style={{ background: "linear-gradient(180deg,#071731 0%,#0A2350 60%,#082042 100%)" }} data-testid="public-player-card">
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4">
        <Link to={`/auction/${auctionId}${team ? `/team/${team.id}` : ""}`} className="text-xs text-yellow-300/80 hover:text-yellow-200 inline-flex items-center gap-1 mb-3">
          <ArrowLeft size={12} /> Back
        </Link>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button type="button" onClick={download} disabled={busy}
            className="rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-4 py-2 text-xs font-black uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-60"
            data-testid="download-player-card">
            <DownloadSimple weight="bold" size={16} />
            {busy ? "Rendering…" : "Download PNG"}
          </button>
          <button type="button" onClick={() => setShowPoints((v) => !v)}
            className="rounded-full border border-white/20 text-white/85 hover:border-yellow-300 hover:text-yellow-300 px-4 py-2 text-xs font-black uppercase tracking-widest flex items-center gap-1.5"
            data-testid="toggle-sold-points" title="Show or hide sold points on the card">
            {showPoints ? <EyeSlash weight="bold" size={16} /> : <Eye weight="bold" size={16} />}
            {showPoints ? "Hide" : "Show"} points
          </button>
        </div>

        {/* Preview wrapper — measures its own width; the card inside is scaled
            with transform so it fits phones. Export ignores this transform. */}
        <div ref={wrapRef} className="mx-auto shadow-2xl" style={{ width: `min(100%, ${CARD_W}px)`, height: Math.round(CARD_H * scale) }}>
          <div
            ref={cardRef}
            data-testid="player-card-canvas"
            style={{
              width: CARD_W,
              height: CARD_H,
              background: "linear-gradient(160deg,#0A2350,#071731)",
              border: `4px solid ${isIcon ? "#F59E0B" : "#FBBF24"}`,
              borderRadius: 20,
              color: "#fff",
              fontFamily: "'Inter','Segoe UI',sans-serif",
              overflow: "hidden",
              position: "relative",
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              boxSizing: "border-box",
            }}>

            {/* Top ribbon — locked height & padding so the tournament name
                and team label sit fully inside the row. */}
            <div style={{
              height: 84,
              padding: "0 28px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "linear-gradient(90deg,#0A2350,#082042)",
              boxSizing: "border-box",
            }}>
              <div style={{
                color: "#FCD34D", fontWeight: 900, textTransform: "uppercase",
                fontSize: 21, letterSpacing: 1.5,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                maxWidth: "60%", lineHeight: 1.3, padding: "8px 0",
              }}>{auction?.name || "AUCTION"}</div>
              {team ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {proxiedTeamLogo && (
                    <img src={proxiedTeamLogo} alt="" crossOrigin="anonymous"
                      style={{ width: 44, height: 44, borderRadius: 6, objectFit: "cover", background: "#fff" }}
                      onError={(e) => { e.target.style.display = "none"; }} />
                  )}
                  <div style={{ color: "#fff", fontWeight: 800, fontSize: 20, lineHeight: 1.3, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "8px 0" }}>
                    {team.short_name || team.name}
                  </div>
                </div>
              ) : (
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, fontWeight: 900, letterSpacing: 3, lineHeight: 1.3, padding: "8px 0" }}>FREE AGENT</div>
              )}
            </div>

            {/* Hero photo area — fixed height so it never clips the name */}
            <div style={{ position: "relative", height: 720, background: "rgba(0,0,0,0.4)", overflow: "hidden" }}>
              {proxiedPhoto ? (
                <img src={proxiedPhoto} alt="" crossOrigin="anonymous"
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }}
                  onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.4)", fontSize: 14 }}>NO PHOTO</div>
              )}
              {/* Number badge */}
              {player.player_number != null && (
                <div style={{
                  position: "absolute", top: 20, left: 20,
                  background: "linear-gradient(135deg,#FBBF24,#F59E0B)",
                  color: "#0F172A", fontWeight: 900, fontSize: 22,
                  padding: "6px 14px", borderRadius: 8,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)", lineHeight: 1.3,
                }}>#{player.player_number}</div>
              )}
              {isIcon && (
                <div style={{
                  position: "absolute", top: 20, right: 20,
                  background: "linear-gradient(135deg,#FBBF24,#F59E0B)",
                  color: "#0F172A", fontWeight: 900, fontSize: 15,
                  padding: "6px 12px", borderRadius: 8, letterSpacing: 2,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)", lineHeight: 1.3,
                }}>★ ICON</div>
              )}
              {player.category && !isIcon && (
                <div style={{
                  position: "absolute", bottom: 130, left: 24,
                  background: "#10B981", color: "#fff", fontWeight: 900,
                  fontSize: 14, padding: "4px 10px", borderRadius: 6, letterSpacing: 2, lineHeight: 1.3,
                }}>{player.category}</div>
              )}
              {/* Name overlay — fixed height gradient so the text sits flush */}
              <div style={{
                position: "absolute", left: 0, right: 0, bottom: 0,
                padding: "80px 30px 24px 30px",
                background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.95) 100%)",
                boxSizing: "border-box",
              }}>
                <div style={{
                  color: "#FCD34D", fontWeight: 900, fontSize: 40,
                  lineHeight: 1.35, paddingBottom: 4,
                  textShadow: "0 4px 8px rgba(0,0,0,0.8)",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}>{player.name}</div>
                {player.role && (
                  <div style={{ color: "rgba(255,255,255,0.9)", fontWeight: 700, fontSize: 17, marginTop: 4, lineHeight: 1.5 }}>{player.role}</div>
                )}
              </div>
            </div>

            {/* Stats grid — 2 columns × 2 rows, each block padded so labels
                and values render at full height (no cropping on export). */}
            <div style={{ padding: "20px 24px 12px 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <StatBlock label="Category" value={player.category || "—"} />
              <StatBlock label="Age" value={player.age || "—"} />
              <StatBlock label="Batting" value={player.batting_style || "—"} />
              <StatBlock label="Bowling" value={player.bowling_style || "—"} />
              {player.location && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <StatBlock label="Location" value={player.location} />
                </div>
              )}
            </div>

            {/* Sold banner — fixed padding + line-height so pts number never
                sits on the top edge. */}
            {showPoints && (
              <div style={{
                margin: "8px 24px 0 24px",
                borderRadius: 12,
                background: "linear-gradient(135deg,#065F46,#059669)",
                border: "1px solid rgba(255,255,255,0.15)",
                padding: "18px 20px 20px 20px",
                textAlign: "center",
                boxSizing: "border-box",
              }}>
                <div style={{ color: "#D1FAE5", fontWeight: 900, fontSize: 13, letterSpacing: 3, lineHeight: 1.4 }}>
                  {player.status === "sold" ? "SOLD FOR" : "BASE"}
                </div>
                <div style={{ color: "#fff", fontWeight: 900, fontSize: 44, marginTop: 4, lineHeight: 1.2 }}>
                  {(pts || 0).toLocaleString()}
                  <span style={{ fontSize: 18, color: "#D1FAE5", marginLeft: 6 }}>pts</span>
                </div>
              </div>
            )}

            {/* Footer branding — locked height so the logo/name never clip */}
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              height: 44, padding: "0 24px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "linear-gradient(90deg,#082042,#0A2350)",
              borderTop: "1px solid rgba(255,255,255,0.06)",
              boxSizing: "border-box",
            }}>
              <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 900, letterSpacing: 3, lineHeight: 1.3 }}>PLAYER CARD</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <img src={proxiedBrand} alt="" crossOrigin="anonymous"
                  style={{ height: 26, display: "block" }}
                  onError={(e) => { e.target.style.display = "none"; }} />
                {!hasCustomBrand && (
                  <span style={{ color: "#FCD34D", fontWeight: 900, fontSize: 12, letterSpacing: 3, lineHeight: 1.3 }}>NITISOFT</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBlock({ label, value }) {
  return (
    <div style={{
      borderRadius: 10,
      background: "#0F2C5A",
      border: "1px solid rgba(255,255,255,0.06)",
      padding: "10px 14px 12px 14px",
      minHeight: 68,
      boxSizing: "border-box",
    }}>
      <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 10, fontWeight: 900, letterSpacing: 2, lineHeight: 1.4 }}>{String(label).toUpperCase()}</div>
      {/* Smaller size + taller line-height so Devanagari/Marathi glyphs
          (matras above & below the baseline) never touch the block edges
          in the rasterised PNG. */}
      <div style={{ color: "#fff", fontSize: 16, fontWeight: 800, marginTop: 4, lineHeight: 1.6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
    </div>
  );
}

function FullPage({ children }) {
  return <div className="min-h-screen grid place-items-center label-mono text-yellow-300/80" style={{ background: "#071731" }}>{children}</div>;
}
